/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { useDisclaimer, useUserId } from '@/api/hooks';
import { getDisclaimerAcknowledgement, saveDisclaimerAcknowledgement } from '@/api/settings';
import { Icon } from '@/components/Icon';
import { useUiStore } from '@/stores/uiStore';
import { Modal } from './Modal';

/** Blocking safety disclaimer: content and acknowledgement state come from the backend only. */
export function DisclaimerModal() {
  const { t, i18n } = useTranslation();
  const gmin = useUserId();
  const hasUser = Boolean(gmin);
  const queryClient = useQueryClient();
  const disclaimerQuery = useDisclaimer(i18n.language, hasUser);

  const ackQuery = useQuery({
    queryKey: ['disclaimer-ack', gmin],
    queryFn: () => getDisclaimerAcknowledgement(gmin),
    enabled: hasUser,
    retry: false,
    staleTime: 5 * 60_000,
  });

  const [accepted, setAccepted] = useState(false);
  const [declined, setDeclined] = useState(false);
  const setDisclaimerBlocking = useUiStore((s) => s.setDisclaimerBlocking);

  const content = disclaimerQuery.data;
  const needsAck = Boolean(ackQuery.data?.needsAcknowledgement);
  // Fail closed on acknowledgement errors; content errors block only when acknowledgement is due.
  const backendDown = hasUser && (ackQuery.isError || (needsAck && disclaimerQuery.isError));
  // Hide UI while the acknowledgement check is pending.
  const settling = hasUser && ackQuery.isPending;

  const showServerDown = hasUser && !accepted && backendDown;
  const showDisclaimer = hasUser && !accepted && !backendDown && needsAck;
  // Block other chrome until checks settle (matches uiStore default).
  const blocking = hasUser && !accepted && (settling || showServerDown || showDisclaimer);

  // Publish blocking state so other chrome (e.g. the quick tour) can wait for it.
  useEffect(() => {
    setDisclaimerBlocking(blocking);
  }, [blocking, setDisclaimerBlocking]);

  if (showServerDown) {
    return (
      <Modal
        /* v8 ignore next -- unreachable: dismissable=false means Modal never calls onClose. */
        onClose={() => {}}
        dismissable={false}
        zIndex="var(--z-overlay-blocking)"
        labelledBy="dcl-down-title"
      >
        <div className="modal dcl-modal dcl-down">
          <div className="modal-head mh-col">
            <Icon
              name="warn"
              size={22}
              style={{ color: 'var(--color-destructive-text)', flex: 'none' }}
            />
            <div className="mh-titles">
              <h2 id="dcl-down-title">{t('disclaimer.serverDown.title')}</h2>
            </div>
          </div>
          <div className="dcl-body">
            <p>{t('disclaimer.serverDown.message')}</p>
          </div>
          <div className="dcl-foot">
            <button
              className="btn btn-pri"
              onClick={() => {
                void disclaimerQuery.refetch();
                void ackQuery.refetch();
              }}
            >
              <Icon name="refresh" size={16} />
              {t('disclaimer.serverDown.retry')}
            </button>
          </div>
        </div>
      </Modal>
    );
  }

  // Only render once the backend has both confirmed an acknowledgement is due and returned content.
  if (!showDisclaimer || !content) return null;

  const accept = () => {
    setAccepted(true);
    /* v8 ignore next -- defensive: the modal only renders when gmin is set, so this never trips. */
    if (!gmin) return;
    void saveDisclaimerAcknowledgement(gmin, true)
      .then(() => queryClient.invalidateQueries({ queryKey: ['disclaimer-ack', gmin] }))
      .catch(() => {
        /* best-effort; `accepted` keeps it hidden for this session, backend retries next load */
      });
  };

  return (
    <Modal
      /* v8 ignore next -- unreachable: dismissable=false means Modal never calls onClose. */
      onClose={() => setDeclined(true)}
      dismissable={false}
      zIndex="var(--z-overlay-blocking)"
      labelledBy="dcl-title"
    >
      <div className="modal dcl-modal">
        <div className="modal-head mh-col">
          <Icon
            name="warn"
            size={22}
            style={{ color: 'var(--color-destructive-text)', flex: 'none' }}
          />
          <div className="mh-titles">
            <h2 id="dcl-title">{content.title}</h2>
            <div className="mh-sub">{content.subtitle}</div>
          </div>
        </div>
        <div className="dcl-body">
          <p className="dcl-danger">{content.danger}</p>
          <ul>
            {content.bullets.map((b) => (
              <li key={b}>{b}</li>
            ))}
          </ul>
        </div>
        {declined && (
          <div className="dcl-note">
            <Icon name="warn" size={16} style={{ flex: 'none' }} />
            {t('disclaimer.declineNote')}
          </div>
        )}
        <div className="dcl-foot">
          <span className="dcl-reasked">{t('disclaimer.reasked')}</span>
          <button className="btn btn-ghost" onClick={() => setDeclined(true)}>
            {t('disclaimer.decline')}
          </button>
          <button className="btn btn-pri" onClick={accept}>
            {t('disclaimer.accept')}
          </button>
        </div>
      </div>
    </Modal>
  );
}
